import React, { type ReactNode } from 'react'
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Spinner, VStack, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { API_BASE_URL } from '../../config/api'
import { useFeatureAccess } from '../../hooks/useFeatureAccess'
import { toaster } from '../ui/toaster-instance'
import { resolveLoginReturnTo } from '../../utils/authRedirect'

interface ProtectedRouteProps {
  children: ReactNode
  redirectTo?: string
  requireAuth?: boolean
  requireAdmin?: boolean
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = '/login',
  requireAuth = true,
  requireAdmin = false,
}) => {
  const { loading, isAuthenticated, currentUser } = useAuth()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [isAdmin, setIsAdmin] = useState<boolean>(false)
  const [adminReady, setAdminReady] = useState<boolean>(() => !requireAdmin)

  useEffect(() => {
    let cancelled = false
    const checkAdmin = async () => {
      if (!requireAdmin) {
        setIsAdmin(true)
        setAdminReady(true)
        return
      }
      setAdminReady(false)
      try {
        if (!currentUser) {
          setIsAdmin(false)
          return
        }
        // Force refresh so newly-set custom claims (e.g., admin) are reflected
        const token = await currentUser.getIdToken(true)
        const res = await fetch(`${API_BASE_URL}/api/v1/auth/permissions`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!cancelled && res.ok) {
          const data = await res.json()
          const claims = (data?.custom_claims || {}) as Record<string, any>
          const roleAdmin = data?.role === 'admin' || claims.admin === true || data?.admin === true
          setIsAdmin(roleAdmin)
          setAdminReady(true)
        } else if (!cancelled) {
          setIsAdmin(false)
          setAdminReady(true)
        }
      } catch {
        if (!cancelled) {
          setIsAdmin(false)
          setAdminReady(true)
        }
      }
    }
    checkAdmin()
    return () => { cancelled = true }
  }, [currentUser, requireAdmin])

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="bg"
      >
        <VStack gap={4}>
          <Spinner
            color="brand.500"
            size="xl"
          />
          <Text color="fg.muted" fontSize="sm">
            Loading...
          </Text>
        </VStack>
      </Box>
    )
  }

  // Redirect if authentication requirement not met
  if (requireAuth && !isAuthenticated) {
    // Save the attempted location for redirect after login
    // Preserve any existing state (like proRequired flag)
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location, ...(location.state || {}) }}
        replace
      />
    )
  }

  // While verifying admin privileges, show a loading state to avoid false redirects
  if (requireAdmin && !adminReady) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="bg"
      >
        <VStack gap={4}>
          <Spinner color="brand.500" size="xl" />
          <Text color="fg.muted" fontSize="sm">Checking permissions...</Text>
        </VStack>
      </Box>
    )
  }

  // Enforce admin when required
  if (requireAdmin && adminReady && !isAdmin) {
    return <Navigate to="/" replace />
  }

  // Redirect if already authenticated and trying to access auth pages
  if (!requireAuth && isAuthenticated) {
    const from = resolveLoginReturnTo(location, searchParams)
    return <Navigate to={from} replace />
  }

  return <>{children}</>
}

// Convenience component for public routes (login, register, etc.)
// Redirects authenticated users away (for login/register pages)
export const PublicRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <ProtectedRoute requireAuth={false}>
      {children}
    </ProtectedRoute>
  )
}

// Component for token-based routes that should be accessible to both
// authenticated and unauthenticated users (e.g., unsubscribe, reminder management)
export const TokenBasedRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { loading } = useAuth()

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="bg"
      >
        <VStack gap={4}>
          <Spinner
            color="brand.500"
            size="xl"
          />
          <Text color="fg.muted" fontSize="sm">
            Loading...
          </Text>
        </VStack>
      </Box>
    )
  }

  // Allow access regardless of authentication status
  return <>{children}</>
}

// New component for routes that require authentication
export const RequireAuth: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <ProtectedRoute requireAuth={true}>
      {children}
    </ProtectedRoute>
  )
}

// Component for routes that require Pro access (also requires authentication)
export const RequireProAccess: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <RequireAuth>
      <RequireProAccessInner>
        {children}
      </RequireProAccessInner>
    </RequireAuth>
  )
}

// Inner component that checks Pro access (assumes user is authenticated)
const RequireProAccessInner: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { plan, features, loading: accessLoading } = useFeatureAccess()
  const location = useLocation()
  
  // Check if user has Pro plan or client_management feature
  const hasProAccess = plan === 'pro' || features.includes('client_management')
  
  // Show loading while checking access
  if (accessLoading) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="bg"
      >
        <VStack gap={4}>
          <Spinner color="brand.500" size="xl" />
          <Text color="fg.muted" fontSize="sm">
            Checking Pro access...
          </Text>
        </VStack>
      </Box>
    )
  }
  
  // Redirect if user doesn't have Pro access
  if (!hasProAccess) {
    return <Navigate to="/dashboard" replace state={{ from: location }} />
  }
  
  return <>{children}</>
}

export default ProtectedRoute 
